/**
 * Le fondamenta non cromatiche: tipografia, ritmo, raggi, profondita', movimento.
 *
 * Sono numeri, non suggerimenti. Una griglia rispettata al 90% non e' una
 * griglia: e' rumore che l'occhio legge come sciatteria senza saper dire perche'.
 */
/**
 * Tipografia.
 *
 * Il logotipo "beste" e' un carattere su misura e non e' un carattere
 * d'interfaccia: le sue grazie sono spesse il 2% dell'altezza e sparirebbero a
 * qualunque corpo di testo. Vive solo dentro gli asset del marchio.
 *
 * Per l'interfaccia usiamo **Inter** (licenza SIL OFL, quindi ridistribuibile e
 * senza canoni -- coerente con la scelta di eliminare ogni servizio a
 * pagamento), nella variante variabile, servita da noi. Nessun collegamento a
 * Google Fonts: e' un terzo che vedrebbe l'indirizzo IP di ogni dipendente a
 * ogni apertura dell'app, ed e' un problema di GDPR prima che di prestazioni.
 */
export const typography = {
    family: {
        /** Testo e interfaccia. */
        sans: "'Inter Variable', 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        /** Codice, identificativi, marche temporali complete. */
        mono: "'JetBrains Mono', ui-monospace, 'SFMono-Regular', 'Cascadia Mono', monospace",
    },
    /**
     * Cifre a larghezza fissa.
     *
     * Vale per ogni colonna numerica: ore lavorate, importi, matricole, date. Con
     * le cifre proporzionali le unita' non si incolonnano e confrontare due righe
     * richiede di leggerle invece di guardarle. In un gestionale HR e' quasi
     * sempre l'operazione principale, quindi la regola e': ogni numero che sta
     * dentro una tabella o accanto a un altro numero usa questa impostazione.
     */
    numeric: "'tnum' 1, 'zero' 1, 'cv05' 1",
    /**
     * Scala tipografica.
     *
     * `size` e `lineHeight` sono in pixel: React Native non conosce i rem, e
     * mantenere una sola unita' evita la doppia tabella di conversione che nel
     * progetto attuale ha prodotto titoli di dimensione diversa fra web e app.
     * Il generatore CSS converte in rem dove serve.
     *
     * Le crenature negative sui corpi grandi sono necessarie: Inter e' disegnata
     * per il testo corrente e sopra i 24px risulta slegata senza una stretta.
     */
    scale: {
        display: { size: 32, lineHeight: 38, weight: 600, tracking: -0.6 },
        h1: { size: 24, lineHeight: 30, weight: 600, tracking: -0.4 },
        h2: { size: 20, lineHeight: 26, weight: 600, tracking: -0.2 },
        h3: { size: 17, lineHeight: 24, weight: 600, tracking: -0.1 },
        /** Il corpo del testo. Tutto il resto e' un'eccezione a questo. */
        body: { size: 15, lineHeight: 22, weight: 400, tracking: 0 },
        bodyStrong: { size: 15, lineHeight: 22, weight: 600, tracking: 0 },
        small: { size: 13, lineHeight: 18, weight: 400, tracking: 0 },
        caption: { size: 12, lineHeight: 16, weight: 500, tracking: 0.1 },
        /** Etichette maiuscolette: intestazioni di colonna, pastiglie di stato. */
        overline: { size: 11, lineHeight: 14, weight: 600, tracking: 0.6 },
    },
};
/**
 * Ritmo verticale e orizzontale, base 4.
 *
 * I nomi sono i valori: `space[12]` sono dodici pixel. Le scale con nomi
 * astratti (`md`, `lg`) costringono a tenere a mente una tabella e finiscono
 * sempre con qualcuno che scrive il numero a mano perche' "serviva un po' meno
 * di lg".
 */
export const space = {
    0: 0,
    2: 2,
    4: 4,
    6: 6,
    8: 8,
    12: 12,
    16: 16,
    20: 20,
    24: 24,
    32: 32,
    40: 40,
    48: 48,
    64: 64,
    80: 80,
};
export const radius = {
    none: 0,
    /** Pastiglie di stato, marcatori piccoli. */
    sm: 4,
    /** Campi, bottoni, celle. Il raggio predefinito. */
    md: 8,
    /** Schede e riquadri. */
    lg: 12,
    /** Fogli modali e finestre. */
    xl: 16,
    /** Cerchi: avatar, punti di notifica. */
    full: 9999,
};
/**
 * Profondita'.
 *
 * Solo per il tema chiaro: in tema scuro il rilievo si ottiene schiarendo il
 * fondale (vedi `dark.surface`), non impilando nero su nero. I livelli sono
 * quattro e bastano; un'interfaccia che ne chiede un quinto ha di solito un
 * problema di gerarchia, non di ombre.
 *
 * La base e' `33,18,10` -- bronze[950] -- e non il quasi-nero neutro: e' la
 * stessa scelta, con le stesse misure, di `light.shadow` in `theme.ts`, dove
 * sta il perche' per esteso. In breve: su fondali caldi un'ombra neutra vira al
 * freddo e stacca la scheda dal foglio come un ritaglio; questa scalda la
 * penombra di tre punti di saturazione, che e' quanto serve a farla sembrare
 * luce e non inchiostro. Chi cambia una delle due basi cambia anche l'altra:
 * due penombre di temperatura diversa nella stessa schermata si vedono.
 */
export const elevation = {
    /** A filo del fondale. */
    flat: 'none',
    /** Schede e righe sollevate. */
    raised: '0 1px 2px rgba(33,18,10,0.06), 0 1px 3px rgba(33,18,10,0.08)',
    /** Menu a tendina, suggerimenti automatici, tooltip. */
    overlay: '0 4px 8px rgba(33,18,10,0.06), 0 8px 24px rgba(33,18,10,0.12)',
    /** Finestre modali e fogli. */
    modal: '0 8px 16px rgba(33,18,10,0.08), 0 24px 48px rgba(33,18,10,0.18)',
};
/**
 * Movimento.
 *
 * L'utente apre questa app decine di volte al giorno per timbrare: ogni
 * animazione la vedra' migliaia di volte. Le durate sono quindi corte per
 * scelta, e tutto quanto supera i 240 ms e' un'eccezione da giustificare.
 *
 * `instant` esiste per rispettare `prefers-reduced-motion`: non si disattivano
 * le transizioni una per una, si azzera la durata.
 */
export const motion = {
    duration: {
        instant: 0,
        /** Ritorni tattili: pressione di un bottone, spunta di una casella. */
        fast: 120,
        /** Il valore predefinito: comparse, evidenziazioni, cambi di stato. */
        normal: 180,
        /** Fogli e pannelli che entrano da un bordo. */
        slow: 240,
        /** Transizioni di schermata. Oltre questo, mai. */
        screen: 320,
    },
    easing: {
        /** Elementi che entrano: partono decisi e si posano. */
        enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
        /** Elementi che escono: si staccano piano e accelerano via. */
        exit: 'cubic-bezier(0.4, 0, 1, 1)',
        /** Cambi di stato su un elemento che resta dov'e'. */
        standard: 'cubic-bezier(0.2, 0, 0, 1)',
    },
    /**
     * Il passo della cascata: quanto ritardo separa un elemento dal successivo
     * quando un gruppo entra in sequenza invece che in blocco.
     *
     * E' un token e non un numero scritto nel foglio di stile perche' la cascata
     * e' un tratto d'identita' quanto le durate. Scritta a mano, deriva: nel
     * pannello ne convivevano cinque velocita' diverse â€” 25, 35, 40, 45, 55 ms â€”
     * e nessuna era sbagliata, ma l'insieme respirava a cinque ritmi.
     *
     * 40 ms per dieci elementi fanno 360 ms di coda: l'occhio legge una sequenza
     * e non un'attesa. Oltre i dieci il ritardo va limitato a mano
     * (`min(var(--i), 10)`), altrimenti la ventesima riga arriva dopo un secondo.
     */
    stagger: 40,
};
/**
 * Aree toccabili e densita'.
 *
 * 48 px e' il minimo Material e supera i 44 pt richiesti da Apple, quindi un
 * solo numero copre entrambe le piattaforme. Vale per l'*area sensibile*, non
 * per il disegno: un'icona da 24 px puo' avere un bersaglio da 48 px attorno, ed
 * e' esattamente cosi' che va fatta.
 *
 * Qui c'e' stato fino al 2026-08-18 anche un `touchGap: 8`, «distanza minima fra
 * due bersagli distinti, per non prenderli per sbaglio». E' stato tolto perche'
 * non lo leggeva nessuno: ne' il pannello ne' l'app, in nessuna delle due forme
 * -- ne' come `--beste-layout-touchGap`, ne' dal TypeScript. Era una regola
 * scritta come se fosse un valore, e un valore non fa rispettare niente: la
 * distanza fra due bersagli la decide il contenitore che li dispone, non un
 * numero che nessuno legge. Il numero era per giunta identico a `space[8]`, cioe'
 * un gradino della scala degli spazi scritto una seconda volta con un altro nome.
 * Se la regola serve davvero, torna come cancello che misura le distanze vere,
 * non come costante.
 */
export const layout = {
    /** Lato minimo di qualunque bersaglio toccabile. */
    touchTarget: 48,
    /** Margine laterale del contenuto su telefono. */
    gutterMobile: 16,
    /** Margine laterale del contenuto su schermo largo. */
    gutterDesktop: 24,
    /** Larghezza massima di una colonna di testo continuo, in pixel (~75 caratteri). */
    proseMax: 680,
    /** Larghezza della barra laterale del pannello web, aperta e ridotta. */
    sidebar: { expanded: 264, collapsed: 72 },
    /** Altezza della barra superiore. */
    topbar: 56,
};
/**
 * Il velo sopra le fotografie delle sedi, e dove si stringe.
 *
 * La tinta e' `accent.veil` (uguale nei due temi, e li' c'e' scritto perche').
 * Qui c'e' solo **quanto** copre a ogni altezza, perche' quella parte non e' un
 * colore: e' un accordo fra il velo e cio' che ci sta sopra.
 *
 * Le sette fotografie hanno luminanze incompatibili -- `reception-scala` e' un
 * interno bianco, `sede-crepuscolo` e' un notturno -- quindi il velo non puo'
 * essere uniforme: dove va il testo deve coprire molto, dove non ne va deve
 * togliersi di mezzo e lasciar vedere la sede. Da cui la forma: pesante in alto
 * (logotipo e frase), leggero al centro (nessun testo, e' la fotografia), di
 * nuovo pesante in fondo (la riga d'uso interno, che e' piccola e in maiuscolo,
 * cioe' la cosa piu' fragile della schermata).
 *
 * I numeri sono verificati, non scelti a occhio: `tools/check-contrast.mjs`
 * ricompone il velo sopra il **bianco pieno** -- la fotografia piu' luminosa
 * che possa mai capitare -- e misura il testo che ci va sopra. Se qualcuno
 * alleggerisce il velo per far vedere meglio una fotografia, la build si ferma.
 *
 * Sono due consumatori: il `linear-gradient` dell'ingresso web e il
 * `LinearGradient` dello splash della app. Stessa scala, stesso risultato.
 */
export const veil = {
    /** Le fermate, dall'alto (0) al basso (1), con l'opacita' della tinta. */
    ramp: [
        { at: 0, alpha: 0.78 },
        { at: 0.22, alpha: 0.7 },
        { at: 0.48, alpha: 0.38 },
        { at: 0.7, alpha: 0.36 },
        { at: 0.82, alpha: 0.62 },
        { at: 0.92, alpha: 0.84 },
        { at: 1, alpha: 0.92 },
    ],
    /**
     * Le due altezze dove finisce del testo, per il controllo di contrasto.
     *
     * Non sono i vertici del velo: sono i punti in cui il testo sta davvero, e
     * quindi i soli in cui il velo debba rispondere di qualcosa. Ricavati dal
     * pannello alto 1080 px con 48 px di margine -- il logotipo e la frase
     * cominciano attorno all'8% dell'altezza, la riga di piede sta al 96%.
     */
    testo: { alto: 0.08, basso: 0.96 },
    /**
     * Quanto Ã¨ pieno il bianco delle due righe che stanno sul velo.
     *
     * Non sono al 100% perchÃ© su un fondale una frase di servizio a piena forza
     * compete con il logotipo, che Ã¨ la cosa che deve vincere. Ma non sono
     * nemmeno libere: sono i due numeri che `check-contrast.mjs` usa per
     * misurare, e sono gli stessi che escono in `tokens.css` per il foglio di
     * stile. Sbiadire la frase nel CSS senza toccare questa riga non si puÃ².
     *
     * La riga di piede Ã¨ **piÃ¹ marcata** della frase e non meno, benchÃ© sia piÃ¹
     * piccola: sta dove il velo Ã¨ al massimo, ed Ã¨ minuscola e in maiuscolo
     * spaziato, cioÃ¨ la cosa piÃ¹ fragile della schermata.
     */
    opacita: { frase: 0.84, piede: 0.78 },
};
/** Punti di rottura, in pixel di CSS. */
export const breakpoint = {
    /** Telefono in verticale. */
    sm: 480,
    /** Tavoletta in verticale, telefono in orizzontale. */
    md: 768,
    /** Tavoletta in orizzontale, portatile piccolo. */
    lg: 1024,
    /** Scrivania. */
    xl: 1280,
    /** Schermo largo: da qui in poi il contenuto smette di allargarsi. */
    xxl: 1600,
};
/**
 * Ordine di sovrapposizione.
 *
 * Elencato qui una volta sola perche' nessuno debba piu' scrivere `z-index:
 * 9999` sperando che basti. Se un livello manca, si aggiunge qui.
 */
export const zIndex = {
    base: 0,
    raised: 10,
    sticky: 100,
    topbar: 200,
    drawer: 300,
    scrim: 400,
    modal: 500,
    dropdown: 600,
    toast: 700,
    tooltip: 800,
};
//# sourceMappingURL=foundations.js.map